import React, { useEffect, useState } from "react";
import { DatePicker, Select } from "antd";
import moment from "moment";
import {
  convertDateToEODTimestamp,
  convertDateToSODTimestamp,
} from "../../../../utils/dateutils";
import { v4 as uuidv4 } from "uuid";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";

const WeeklyFrequencyControl = ({
  setCarePlan,
  options,
  visitStartDate,
  carePlan,
  readOnly,
}) => {
  const [visitFrequency, setVisitFrequency] = useState([
    { id: 1, visitFrequency: "1-2x", range: [visitStartDate, null] },
  ]);

  const referenceDate = visitStartDate ? moment(visitStartDate) : moment();
  const [controls, setControls] = useState([
    {
      id: 1,
      selectOptions: options,
      value: ["1-2x"],
      range: [referenceDate, null],
      disabledDate: (current) => {
        return current && current < referenceDate.startOf("day");
      },
    },
  ]);

  const handleFrequencyChange = (id, value) => {
    const updatedControls = controls.map((control) =>
      control.id === id ? { ...control, value: value } : control
    );
    setControls(updatedControls);
    updateFrequencyData(updatedControls);
  };

  const handleRangeChange = (dates, dateStrings, id) => {
    const updatedControls = controls.map((control) =>
      control.id === id
        ? {
            ...control,
            range: [moment(dateStrings[0]), moment(dateStrings[1])],
          }
        : control
    );

    setControls(updatedControls);
    updateFrequencyData(updatedControls);
  };

  const updateFrequencyData = (controlsList) => {
    const formattedData = controlsList.map((item) => {
      return {
        visitFrequency: Array.isArray(item.value) ? item.value[0] : item.value,
        id: item.id,
        range: item.range
          ? [
              convertDateToSODTimestamp(item?.range[0]),
              convertDateToEODTimestamp(item?.range[1]),
            ]
          : [],
      };
    });

    setVisitFrequency(formattedData);
    setCarePlan((prev) => ({ ...prev, frequency: formattedData }));
  };

  const handleAddControl = () => {
    if (!readOnly) {
      const lastFrequencyDate =
        visitFrequency[visitFrequency.length - 1]?.range[1];
      const frequencyDate = lastFrequencyDate
        ? moment(lastFrequencyDate)
        : moment();
      if (
        visitFrequency[visitFrequency.length - 1].visitFrequency &&
        visitFrequency[visitFrequency.length - 1].range.every((item) =>
          Boolean(item)
        )
      ) {
        setControls([
          ...controls,
          {
            id: uuidv4(),
            selectOptions: options,
            disabledDate: (current) => {
              return current && current < frequencyDate.endOf("day");
            },
          },
        ]);
      }
    }
  };

  const handleRemoveControl = (id) => {
    if (!readOnly) {
      if (controls.length > 1) {
        setControls(controls.filter((control) => control.id !== id));

        const formattedData = visitFrequency.filter((item) => item.id !== id);
        setVisitFrequency(formattedData);
        setCarePlan((prev) => ({ ...prev, frequency: formattedData }));
      }
    }
  };

  useEffect(() => {
    const carePlanFrequency =
      carePlan?.frequencyPeriod === "weekly" &&
      carePlan?.frequency?.[0]?.range &&
      carePlan?.frequency;
    if (carePlanFrequency) {
      const carePlanControl = carePlanFrequency?.map((item) => {
        const controlObj = {
          id: item?.id,
          selectOptions: options,
          value: [item?.visitFrequency],
          range: item?.range?.map((date) => moment(date)),
          disabledDate: (current) => {
            return current && current < referenceDate.startOf("day");
          },
        };
        return controlObj;
      });
      setControls(carePlanControl);

      const carePlanVisitFrequency = carePlanFrequency?.map((item) => {
        return {
          id: item?.id,
          visitFrequency: [item?.visitFrequency],
          range: item.range,
        };
      });
      setVisitFrequency(carePlanVisitFrequency);
    } else {
      setControls([
        {
          id: 1,
          selectOptions: options,
          value: ["1-2x"],
          range: [referenceDate, null],
          disabledDate: (current) => {
            return current && current < referenceDate.startOf("day");
          },
        },
      ]);
    }
  }, [options, visitStartDate]);

  return (
    <>
      {controls.map((control, index) => (
        <div key={control.id} className="mt-2 d-flex align-items-center">
          <Select
            onChange={(value) => handleFrequencyChange(control.id, value)}
            options={control.selectOptions}
            value={control.value}
            placeholder="Frequency"
            style={{
              width: 120,
            }}
            className="me-3"
            disabled={readOnly}
          />

          <DatePicker.RangePicker
            disabledDate={control.disabledDate}
            value={control.range}
            onChange={(dates, dateStrings) =>
              handleRangeChange(dates, dateStrings, control.id)
            }
            format="MM/DD/YYYY"
            allowClear={false}
            disabled={readOnly}
          />

          {!readOnly && index !== 0 && (
            <div className="add-text ms-3">
            <SmartIcon iconKey={"reject"} handleIconClick={() => handleRemoveControl(control.id)}/>
          </div>
          )}
        </div>
      ))}
      {!readOnly && (
        <div className="mt-2 add-text">
          <span onClick={handleAddControl}>Add Frequency</span>
        </div>
      )}
    </>
  );
};

export default WeeklyFrequencyControl;
